<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id
      }"
    />
    <Multiselect
      :id="id"
      ref="multiselect"
      :key="'user-select'+ selectKey"
      :value="value"
      :customLabel="customLabel"
      :placeholder="disabled ? '—' : 'Type to search for users'"
      open-direction="bottom"
      :options="options"
      :loading="isLoading"
      :internal-search="false"
      :limit-text="limitText"
      :show-no-options="true"
      :show-no-results="true"
      :multiple="mergedConfig.multiple"
      :disabled="disabled"
      :data-test="dataTest"
      @input="update"
      @search-change="asyncFind"
    >
      <template slot="noOptions">
        Search to find users.
      </template>

      <template slot="noResult">
        No users found.
      </template>
    </Multiselect>
  </div>
</template>

<script>
import FormMixin from './FormMixin'
import { mapGetters } from 'vuex'
import { getUsersSearch, getUsers } from '@/api'
import _debounce from 'lodash/debounce'
import Multiselect from '@/components/Multiselect'
import { DEBOUNCE_INTERVAL } from '@/constants/'
import FormLabel from './FormLabel'

export default {
  mixins: [ FormMixin ],
  components: { Multiselect, FormLabel },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    schoolOverride: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      searchOptions: [],
      usersData: {},
      isLoading: false,
      searchQuery: null,
      selectKey: 0,
    }
  },
  computed: {
    ...mapGetters([ 'school', 'userById', 'user' ]),
    mergedConfig () {
      return {
        multiple: true,
        ...this.config,
      }
    },
    searchConfig () {
      const config = {
        school: this.schoolOverride || this.school,
        searchQuery: this.searchQuery,
        returnArray: true,
      }
      return config
    },
    normalizedFetchValue () {
      return (Array.isArray(this.value) ? this.value : [ this.value ]).filter(Boolean)
    },
    options () {
      return this.searchOptions
    },
  },
  async created () {
    const missingValues = this.normalizedFetchValue.filter(el => !this.userById(el)._id)
    if (missingValues.length) {
      await this.fetchUsers(missingValues)
    }
  },
  methods: {
    async fetchUsers (list = []) {
      if (!list.length) return
      const config = {
        school: this.schoolOverride || this.school,
        list: list.join(','),
      }
      const users = await getUsers(config) || {}
      this.usersData = {
        ...this.usersData,
        ...users,
      }
      // increment this value to force multiselect to re-render, which will update labels
      this.selectKey++
    },
    getUser (id) {
      return this.usersData[id]
    },
    limitText (count) {
      return `and ${count} other users`
    },
    customLabel (value) {
      let user
      if (value === this.user._id) user = this.user
      else user = this.getUser(value) || this.userById(value)
      return user._id ? this.getFullName(user) : value
    },
    update (value) {
      const arrayValue = Array.isArray(value) ? value : [ value ]
      arrayValue.forEach(v => {
        const user = this.getUser(v)
        if (user) {
          this.$store.commit('SET_USER', user)
        }
      })
      this.$emit('input', value)
    },
    asyncFind (query) {
      if (query) {
        this.isLoading = true
        this.getUsersDebounced(query)
      } else {
        this.isLoading = false
      }
    },
    getUsersDebounced: _debounce(function (query) {
      this.getUsers(query)
    }, DEBOUNCE_INTERVAL),
    getUsers (query) {
      this.searchQuery = query
      getUsersSearch(this.searchConfig).then(data => {
        if (!Array.isArray(data)) {
          this.searchOptions = []
        } else {
          this.usersData = Object.assign({}, this.usersData, ...data.map(el => ({ [el._id]: el })), {})
          this.searchOptions = data.map(el => el._id)
        }
        this.isLoading = false
      })
    },
  },
}
</script>

<style scoped>
.form-control {
  height: 36px;
}
.multiselect >>> .multiselect__tag {
  white-space: normal;
}
.multiselect >>> .multiselect__tags {
  overflow: hidden;
}
</style>
